/**
 * Country → city URL schema for SEO pages (single source: geo.cities.json).
 * English-only paths; add /{lang}/ when localized content exists.
 */

import geoCitiesJson from './geo.cities.json';

export type GeoCityConfig = {
    countrySlug: string;
    countryName: string;
    citySlug: string;
    cityName: string;
    /** Mongo `Place.city` spellings (diacritics / EN variants). Falls back to cityName. */
    mongoCityNames?: string[];
    restaurantsDataKey: string;
    whereToEatGuideKey?: string;
    /** When true, included in sitemap and public city link lists */
    seoPublished?: boolean;
    featuredRestaurantPaths?: string[];
};

export const GEO_CITIES: GeoCityConfig[] = geoCitiesJson as GeoCityConfig[];

/** Cities live for SEO (sitemap, homepage links). Flip seoPublished in geo.cities.json to launch more. */
export const SEO_PUBLISHED_CITIES = GEO_CITIES.filter((g) => g.seoPublished === true);

export const ALLOWED_COUNTRY_SLUGS = [...new Set(GEO_CITIES.map((g) => g.countrySlug))];

export const SITE_ORIGIN = 'https://solt.app';

export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/solt-main-og3.jpg`;

export function findGeoByCountryCity(
    countrySlug?: string,
    citySlug?: string
): GeoCityConfig | undefined {
    if (!countrySlug || !citySlug) return undefined;
    const country = countrySlug.toLowerCase();
    const city = citySlug.toLowerCase();
    return GEO_CITIES.find((g) => g.countrySlug === country && g.citySlug === city);
}

export function getMongoCityNames(geo: GeoCityConfig): string[] {
    if (geo.mongoCityNames?.length) {
        return geo.mongoCityNames;
    }
    return [geo.cityName];
}

export function geoCityPath(geo: GeoCityConfig): string {
    return `/${geo.countrySlug}/${geo.citySlug}`;
}

/** Best restaurants list — path includes target keyword for SEO. */
export function geoBestRestaurantsPath(geo: GeoCityConfig): string {
    return `${geoCityPath(geo)}/best-restaurants`;
}

export function absoluteUrl(path: string): string {
    return `${SITE_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Indexable paths for a published city (hub + best-restaurants only). */
export function geoSitemapPaths(geo: GeoCityConfig): string[] {
    return [geoCityPath(geo), geoBestRestaurantsPath(geo)];
}
